import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Select, Modal, ScrollArea, SimpleGrid } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllHscodes, deleteHscode } from '../../../../services/hscodes'
import { getAllCountryList } from '../../../../services/location'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconPlus } from '@tabler/icons-react'
import { modals } from '@mantine/modals'
import CardHscode from './CardHscode'
import FormHscode from './FormHscode'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 12,
    sort: '-id',
}

const Hscodes = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [dataList, setDataList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [detailData, setDetailData] = useState(null)
    const [allCountries, setAllCountries] = useState([])
    const [loadingCountries, setLoadingCountries] = useState(false)

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'Hscodes', route: null },
    ]

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))
        fetchCountries()
        // eslint-disable-next-line
    }, [dispatch])

    const fetchCountries = async () => {
        setLoadingCountries(true)
        try {
            const response = await getAllCountryList({ 'page[limit]': 300 })
            if (response && response.data) {
                const mapped = response.data.map(c => ({
                    value: String(c.attributes.id),
                    label: c.attributes.name
                }))
                setAllCountries(mapped)
            }
        } catch (error) {
            console.error("Failed to fetch countries", error)
        } finally {
            setLoadingCountries(false)
        }
    }

    const handleDelete = (val) => {
        modals.openConfirmModal({
            title: 'Delete HS Code',
            children: (
                <Text size="sm">
                    Are you sure you want to delete HS Code <strong>{val.code}</strong>? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onCancel: () => console.log('Cancel'),
            onConfirm: async () => {
                try {
                    const response = await deleteHscode(val.id)
                    if (response.success) {
                        notifications.show({
                            title: 'Success',
                            message: 'HS Code deleted successfully',
                            color: 'teal',
                            icon: <IconCheck />,
                        })
                        handleGetAllHscodesMapped()
                    } else {
                        notifications.show({
                            title: 'Failed',
                            message: response.message || 'Failed to delete HS Code',
                            color: 'red',
                            icon: <IconX />,
                        })
                    }
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error.message || 'Something went wrong',
                        color: 'red',
                        icon: <IconX />,
                    })
                }
            },
        })
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleChangePage = (val) => {
        setParams((oldVal) => ({ ...oldVal, 'page[num]': val }))
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    const handleAdd = () => {
        setDetailData(null)
        setOpenModalForm(true)
    }

    const handleEdit = (val) => {
        setDetailData(val)
        setOpenModalForm(true)
    }

    const handleGetAllHscodesMapped = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllHscodes(params)

            if (response.success || response.data) {
                const startData = response.data || []
                const countData = response.meta?.pagination?.total || 0

                const mappedData = startData.map(item => {
                    const attr = {...(item.attributes || {}) }
                    return {
                        id: item.id || attr.id,
                        country_id: attr.country_id,
                        native_language: attr.native_language,
                        code: attr.code,
                        description: attr.description,
                        native_description: attr.native_description,
                        taxes: attr.taxes,
                        total_tax: attr.total_tax,
                    }
                })

                setDataList(mappedData)
                setTotalData(countData)
            } else {
                setDataList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setDataList([])
        } finally {
            setLoading(false)
        }
    }, 500)


    // Use the mapped version
    useEffect(() => {
        handleGetAllHscodesMapped()
        // eslint-disable-next-line
    }, [params])


    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>HS Codes</Text>
                        <Group gap='sm' align='center'>
                            {!loadingCountries && (
                                <Select
                                    placeholder="Filter Country"
                                    data={allCountries}
                                    value={params['filter[country_id]'] || null}
                                    onChange={(val) => {
                                        handleChangeFilter('filter[country_id]', val)
                                        handleChangePage(1)
                                    }}
                                    searchable
                                    clearable
                                    size='xs'
                                    w={200}
                                />
                            )}
                            <TextInput
                                value={params['filter[code][like]'] || ''}
                                placeholder='Search by Code'
                                onChange={(val) => {
                                    handleChangeFilter('filter[code][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <TextInput
                                value={params['filter[description][like]'] || ''}
                                placeholder='Search by Description'
                                onChange={(val) => {
                                    handleChangeFilter('filter[description][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Button size='xs' leftSection={<IconPlus size={14} />} onClick={handleAdd}>Add HS Code</Button>
                        </Group>
                    </Flex>
                </Box>

                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} HS Codes </Text>
                    </Group>

                    {loading ? (
                        <LoadingData />
                    ) : dataList.length > 0 ? (
                        <SimpleGrid cols={{ base: 1, sm: 2, lg: 2 }} spacing="md">
                            {dataList.map((item) => (
                                <CardHscode
                                    key={item.id}
                                    data={item}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </SimpleGrid>
                    ) : (
                        <NoData />
                    )}
                </Box>
                <Flex justify='end'>
                    <Pagination
                        size='sm'
                        onChange={handleChangePage}
                        value={params['page[num]']}
                        total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0}
                    />
                </Flex>
            </Box>

            <Modal
                opened={openModalForm}
                onClose={onCloseModalForm}
                centered
                closeOnClickOutside={false}
                size='lg'
                title={<Text fw='Bold'>{detailData ? 'Edit HS Code' : 'Add HS Code'}</Text>}
                scrollAreaComponent={ScrollArea.Autosize}
                withCloseButton={false}
            >
                <FormHscode
                    dataDetail={detailData}
                    onCloseModal={onCloseModalForm}
                    reloadList={handleGetAllHscodesMapped}
                />
            </Modal>
        </AuthLayout>
    )
}

export default Hscodes
